import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import WorkIcon from '@mui/icons-material/Work';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import DailyCalendar from '../components/calendar/DailyCalendar';
import WeeklyCalendar from '../components/calendar/WeeklyCalendar';
import WorkingWeekCalendar from '../components/calendar/WorkingWeekCalendar';
import { getCalendarOverviewPath } from '../constants/routes';

/**
 * Calendar View Page - Main calendar with multiple view options
 */
const CalendarViewPage = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const selectedCalendar = location.state?.selectedCalendar || null;
  const [loading] = useState(false);
  const [error] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Default based on screen size
    return isMobile ? 'day' : 'week';
  });

  // Auto-switch view mode when screen size changes
  useEffect(() => {
    if (isMobile && viewMode !== 'day') {
      setViewMode('day');
    } else if (!isMobile && viewMode === 'day') {
      setViewMode('week');
    }
  }, [isMobile, viewMode]);

  const handleBack = () => {
    navigate(getCalendarOverviewPath(customerId));
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading calendar..." />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} size={isMobile ? 'small' : 'medium'}>
          {t('common.backToCalendars')}
        </Button>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          aria-label="calendar view"
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          {isMobile ? [
            <ToggleButton key="day" value="day" aria-label="day view">
              <ViewDayIcon sx={{ mr: { xs: 0.5, sm: 1 } }} />
              {t('common.day')}
            </ToggleButton>
          ] : [
            <ToggleButton key="working-week" value="working-week" aria-label="working week view">
              <WorkIcon sx={{ mr: 1 }} />
              {t('common.workingWeek')}
            </ToggleButton>,
            <ToggleButton key="week" value="week" aria-label="week view">
              <ViewWeekIcon sx={{ mr: 1 }} />
              {t('common.fullWeek')}
            </ToggleButton>
          ]}
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'day' && (
        <DailyCalendar 
          customerId={customerId} 
          selectedCalendar={selectedCalendar} 
        />
      )}
      
      {viewMode === 'working-week' && (
        <WorkingWeekCalendar 
          customerId={customerId} 
          selectedCalendar={selectedCalendar} 
        />
      )}
      
      {viewMode === 'week' && (
        <WeeklyCalendar 
          customerId={customerId} 
          selectedCalendar={selectedCalendar} 
        />
      )}
    </Container>
  );
};

export default CalendarViewPage;
